import { Container, Grid } from '@mui/material';
import { createColumnHelper } from '@tanstack/react-table';
import { useFlags } from 'launchdarkly-react-client-sdk';
import * as _ from 'lodash';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';

import { treeViewUtils } from '@/utils';
import VNAutocomplete from '@/components/VNAutocomplete/VNAutocomplete';

import type { Pagination } from '../../models';
import { enhanceFetchedAssets } from '../../pages/assets/asset-listing/enhanceFetchedAsset';
import { FuelPortalService } from '../../services';
import { AllAssetFilterTypeOptions, type AssetFilterTypeOptions } from '../../store/forms/Asset-Editor/AssetFilterTypeOptions';
import { applicationDataSelector } from '../../store/slices/application-data';
import { fuelUserAccessSliceSelector } from '../../store/slices/fuelUserAccess';
import clientUtils from '../../utils/clientUtils';
import { dateUtils } from '../../utils/dateUtils';
import itemUtils from '../../utils/itemUtils';
import FuelSearchInput from '../fuel-search-input';
import FuelTags from '../fuel-tags';
import FuelAssetThumbnail from '../FuelAssetThumbnail/FuelAssetThumbnail';
import FuelAssetTitle from '../FuelAssetTitle/FuelAssetTitle';
import type { DropdownOption } from '../VNDropdown';
import { VNDropdown, VNDropdownOUOptions } from '../VNDropdown';
import VNTable from '../VNTable/VNTable';

const initialPagination: Pagination = {
  sortBy: 'publicationDate',
  descending: true,
  rowsPerPage: 100,
  page: 1,
  totalItems: 0,
  pages: 1,
};

interface Iprops {
  showEmbargoColumns?: boolean;
  isBroadcastChannel?: boolean;
  channelVariants?: string[];
  assetFilterTypes?: AssetFilterTypeOptions[];
  excludeAssetFilterTypes?: AssetFilterTypeOptions[];
  showStatus?: boolean;
  allowSeriesAndSeasons?: boolean;
}

const renderAssetTypeOption = (option: AssetFilterTypeOptions & { label?: string }) => (
  <>
    <span className="vn-dropdown-list__label" title={option.label ?? option.name}>
      {option.label ?? option.name}
    </span>
    {option.icon && option.iconAlign === 'right' ? (
      <span className="vn-dropdown-list__icon vn-dropdown-list__icon--right">{option.icon}</span>
    ) : null}
  </>
);

const FuelAssetSearch = ({
  showEmbargoColumns = false,
  isBroadcastChannel = false,
  channelVariants = [],
  assetFilterTypes = [],
  excludeAssetFilterTypes = [],
  showStatus = false,
  allowSeriesAndSeasons = false,
}: Iprops) => {
  // const [pagination, setPagination] = useState<Pagination>(null);
  const { ouData, schemas, categories } = useSelector(applicationDataSelector);
  // const [selectedCategories, setSelectedCategories] = useState<any>([]);
  // const [selectedTags, setSelectedTags] = useState<any>([]);
  // const [items, setItems] = useState<any>([]);
  // const [searchKeyword, setSearchKeyword] = useState('');
  const [selectedAssetFilterTypes, setSelectedAssetFilterTypes] = useState([]);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [tableLoading, setTableLoading] = useState(false);
  const { permissions, stage, userAccessLoadingLoading, accessibleOUs, fuelUserAccessData } =
    useSelector(fuelUserAccessSliceSelector);
  const flags = useFlags();
  const [ouOptions, setOuOptions] = useState<DropdownOption[]>([]);
  const ouForm = useForm<{ ouSelect: (string | number)[] }>({ defaultValues: { ouSelect: [] } });
  const categoryForm = useForm<{ selectedCategories: string[] }>({ defaultValues: { selectedCategories: [] } });
  const assetFilterForm = useForm<{ selectedAssetFilterTypes: string[] }>({
    defaultValues: { selectedAssetFilterTypes: [] },
  });

  const vnTableMethods = useForm({
    defaultValues: {
      filters: { searchText: '', status: '', showThumbnail: true },
      selectedRows: [],
      pagination: initialPagination,
      data: [], // Store your table data here
    },
  });

  const accessibleOUList = useMemo(() => {
    const currentOuId = ouData.ouId ?? ouData.id;

    const childOUs = accessibleOUs.filter((ou) => ou.parentOuId === currentOuId);
    if (childOUs.length === 0) {
      setOuOptions([]);
      return [];
    }

    const filteredOUs = treeViewUtils.getAccessibleOusForPortalSubtree(accessibleOUs, currentOuId, 0);
    const flatOptions: DropdownOption[] = treeViewUtils.createDataTreeArray(filteredOUs).map((item) => ({
      id: item.ou.ouId,
      label: item.ou.ouName,
      depth: item.depth,
      childCount: item.childCount,
    }));
    setOuOptions(flatOptions);
    return flatOptions;
  }, [accessibleOUs]);

  const stateRef = useRef({
    searchText: '',
    selectedCategories: [] as Array<{ name: string }>,
    selectedTags: [] as string[],
    selectedOUIds: [] as string[],
  });
  const doSearchRef = useRef<() => Promise<void>>(() => Promise.resolve());
  const searchGenerationRef = useRef(0);
  const ouInitializedRef = useRef(false);

  const filterSearchDebounced = useMemo(
    () =>
      _.debounce(() => {
        void doSearchRef.current();
      }, 300),
    []
  );

  const throttledCallback = useMemo(
    () =>
      _.debounce(() => {
        void doSearchRef.current();
      }, 0),
    []
  );

  useEffect(
    () => () => {
      filterSearchDebounced.cancel();
      throttledCallback.cancel();
    },
    [filterSearchDebounced, throttledCallback]
  );

  useEffect(() => {
    void doSearchRef.current();
  }, []); // <-- empty dependency array

  const watchedOuSelect = ouForm.watch('ouSelect');
  useEffect(() => {
    if (!ouInitializedRef.current) {
      ouInitializedRef.current = true;
      return;
    }
    stateRef.current.selectedOUIds = (watchedOuSelect ?? []) as string[];
    resetPaginationToFirstPage();
    void doSearchRef.current();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchedOuSelect]);

  const getPagination = () => {
    // reset the pagination to the first page
    let paginationNew = vnTableMethods.getValues('pagination');
    if (!paginationNew) {
      paginationNew = _.cloneDeep(initialPagination);
      paginationNew.page = 1;
    }
    return paginationNew;
  };

  // Reset pagination back to the first page when the search criteria changes.
  // Page navigation via the table's pagination control is intentionally left alone.
  const resetPaginationToFirstPage = () => {
    const paginationObject = vnTableMethods.getValues('pagination');
    if (paginationObject) {
      paginationObject.page = 1;
      paginationObject.totalItems = 0;
      paginationObject.pages = 1;
      vnTableMethods.setValue('pagination', paginationObject);
    }
  };

  const columnHelper = createColumnHelper<any>();

  const Thumbnail = useCallback(
    (cell) => <FuelAssetThumbnail item={cell.row.original} permissions={permissions} hideStatus={!showStatus} />,
    [permissions, showStatus]
  );

  const columns = useMemo(() => {
    // 1. Handle common columns/logic if possible, or define within scopes
    const embargoColumns = [
      columnHelper.accessor('thumbnailSrc', {
        header: '',
        cell: Thumbnail,
      }),
      columnHelper.accessor('title', {
        align: 'left',
        header: () => 'Name',
        size: 170,
        meta: {
          sortable: false,
          locked: true, //
          headerTitle: 'Title',
          style: {
            textAlign: 'left',
          },
          contentStyle: {
            justifyContent: 'flex-start',
          },
        },
        cell: (cell) => <div title={cell.getValue()}>{cell.getValue()}</div>,
      }),
      columnHelper.accessor('embargoDate', {
        header: () => `Embargo (${ouData.timezoneAbbreviation})`,
        size: 130,
        meta: {
          sortable: true,
          locked: false, //
          headerTitle: 'Embargo',
        },
        cell: (cell) => dateUtils.format(cell.getValue(), ouData.timezone),
      }),
      columnHelper.accessor('expireDate', {
        header: `Expiry (${ouData.timezoneAbbreviation})`,
        size: 130,
        meta: {
          sortable: true,
          locked: false, //
          headerTitle: 'Expiry',
        },
        cell: (cell) => dateUtils.format(cell.getValue(), ouData.timezone),
      }),
      columnHelper.accessor('durationString', {
        header: 'Duration',
        size: 90,
        meta: {
          sortable: true,
          locked: false, //
          headerTitle: 'Duration',
        },
        cell: (cell) => clientUtils.formatDuration(cell.row.original.duration),
      }),
    ];

    const defaultColumns = [
      columnHelper.accessor('thumbnailSrc', {
        header: '',
        cell: Thumbnail,
      }),
      columnHelper.accessor('title', {
        align: 'left',
        header: () => 'Title',
        size: 170,
        meta: {
          sortable: true,
          locked: true, //
          headerTitle: 'Title',
          style: {
            textAlign: 'left',
          },
          contentStyle: {
            justifyContent: 'flex-start',
          },
        },
        cell: (cell) => <FuelAssetTitle item={cell.row.original} permissions={permissions} />,
      }),
      columnHelper.accessor('publicationDate', {
        header: `Publication (${ouData.timezoneAbbreviation})`,
        size: 180,
        meta: {
          sortable: false,
          locked: false, //
          headerTitle: 'Publication',
        },
        cell: (cell) => dateUtils.format(cell.getValue(), ouData.timezone),
      }),
      columnHelper.accessor('categoryString', {
        header: 'Categories',
        footer: (props) => props.column.id,
        size: 130,
        meta: {
          sortable: true,
          locked: false, //
          headerTitle: 'Categories',
        },
        cell: (cell) => (
          <div className="title" title={cell.getValue()}>
            {cell.getValue()}
          </div>
        ),
      }),
      columnHelper.accessor('tagString', {
        header: 'Tags',
        footer: (props) => props.column.id,
        meta: {
          sortable: true,
          locked: false, //
          headerTitle: 'Tags',
          // style: {
          //   textAlign: 'left',
          // },
          // contentStyle: {
          //   justifyContent: 'flex-start',
          // },
        },
        size: 130,
        cell: (cell) => (
          <div title={cell.getValue()} className="title">
            {cell.getValue()}
          </div>
        ),
      }),
      columnHelper.accessor('durationString', {
        header: 'Duration',
        size: 90,
        meta: {
          sortable: true,
          locked: false, //
          headerTitle: 'Duration',
        },
        cell: (cell) => clientUtils.formatDuration(cell.row.original.duration),
      }),
    ];

    const broadcastChannelColumns = [
      columnHelper.accessor('thumbnailSrc', {
        header: '',
        cell: Thumbnail,
      }),
      columnHelper.accessor('title', {
        align: 'left',
        header: () => 'Title',
        size: 170,
        meta: {
          sortable: false,
          locked: true, //
          headerTitle: 'Title',
          style: {
            textAlign: 'left',
          },
          contentStyle: {
            justifyContent: 'flex-start',
          },
        },
        cell: (cell) => <FuelAssetTitle item={cell.row.original} permissions={permissions} />,
      }),
      columnHelper.accessor('durationString', {
        header: 'Duration',
        size: 90,
        meta: {
          sortable: true,
          locked: false, //
          headerTitle: 'Duration',
        },
        cell: (cell) => clientUtils.formatDuration(cell.row.original.duration),
      }),
    ];

    // 2. Return logic
    if (isBroadcastChannel) return broadcastChannelColumns;
    if (showEmbargoColumns) return embargoColumns;
    return defaultColumns;
  }, [columnHelper, showEmbargoColumns, isBroadcastChannel, ouData, permissions]);

  const doSearch = async () => {
    const generation = ++searchGenerationRef.current;
    // console.log('doSearch', { excludeAssetFilterTypes });
    setTableLoading(true);
    try {
    const myPagination = getPagination();
    const { searchText, selectedCategories, selectedTags } = stateRef.current;
    const categoryNames = (selectedCategories ?? []).map((c) => c.name);
    const assetFilterTypes = _.filter(AllAssetFilterTypeOptions, (item) => selectedAssetFilterTypes.includes(item.id));

    const resolveSortBy = () => {
      switch (myPagination.sortBy) {
        case 'categoryString':
          return 'categories';
        case 'tagString':
          return 'tags';
        case 'durationString':
          return 'duration';
        default:
          return myPagination.sortBy;
      }
    };

    // #2916 always exclude series and season by default
    // if (!allowSeriesAndSeasons) {
    //   excludeAssetFilterTypes.push({ id: AssetFilterType.Collection, name: AssetFilterType.Collection });
    // }
    const searchExcludeAssetFilterTypes = _.uniqBy(excludeAssetFilterTypes, 'id');
    const partnerIds = stateRef.current.selectedOUIds.length > 0 ? stateRef.current.selectedOUIds : [];
    const results = await FuelPortalService.searchAsset(
      searchText,
      ouData.id,
      partnerIds,
      myPagination.page - 1,
      myPagination.rowsPerPage,
      categoryNames,
      selectedTags,
      resolveSortBy(),
      myPagination.descending ? 'desc' : 'asc',
      true,
      'clip',
      'Video',
      false,
      assetFilterTypes,
      searchExcludeAssetFilterTypes
    );
    const paginationObj = myPagination;
    if (results && results.data) {
      const thumbnails = await itemUtils.fetchLinkedDisplayThumbnails(results.data);
      const assets = enhanceFetchedAssets(results.data, permissions, flags, true, channelVariants, [], ouData, null, null, thumbnails);

      paginationObj.totalItems = results.pagination.total;
      paginationObj.pages = Math.ceil(paginationObj.totalItems / paginationObj.rowsPerPage);
      // setPagination(paginationObj);
      // setItems(assets);
      if (generation !== searchGenerationRef.current) return;
      vnTableMethods.setValue('data', assets);
      vnTableMethods.setValue('pagination', paginationObj);
    } else {
      paginationObj.totalItems = 0;
      paginationObj.pages = 0;
      // setPagination(paginationObj);
      // setItems([]);
      if (generation !== searchGenerationRef.current) return;
      vnTableMethods.setValue('data', []);
      vnTableMethods.setValue('pagination', paginationObj);
    }
    } finally {
      if (generation === searchGenerationRef.current) {
        setTableLoading(false);
      }
    }
  };

  doSearchRef.current = doSearch;

  const paginationChange = (value: Pagination) => {
    // console.log('paginationChange', value);
    // setPagination(value);
    vnTableMethods.setValue('pagination', value);
    void doSearchRef.current();
  };

  const onCategoriesChanged = (categoryIds: string | string[]) => {
    const ids = Array.isArray(categoryIds) ? categoryIds : [];
    categoryForm.setValue('selectedCategories', ids);
    stateRef.current.selectedCategories = ids
      .map((id) => {
        const match = categories?.find((c) => c.id === id);
        return match ? { name: match.name } : null;
      })
      .filter((entry): entry is { name: string } => entry != null);
    resetPaginationToFirstPage();
    throttledCallback();
  };

  const categoryOptions = useMemo(
    () =>
      (categories ?? []).map((c) => ({
        id: c.id,
        label: c.name,
      })),
    [categories]
  );

  const onTagsChanged = (value) => {
    // console.log('onTagsChanged');
    stateRef.current.selectedTags = value;
    setSelectedTags(value);
    resetPaginationToFirstPage();
    filterSearchDebounced();
  };

  const onSearchClick = (value) => {
    // console.log('onSearchClick');
    stateRef.current.searchText = value;
    resetPaginationToFirstPage();
    filterSearchDebounced.cancel();
    void doSearchRef.current();
  };

  const onSearchTextChanged = (value) => {
    // FuelSearchInput already debounces onChange by 300ms — search immediately here.
    stateRef.current.searchText = value;
    resetPaginationToFirstPage();
    void doSearchRef.current();
  };

  const onAssetFilterTypesChanged = (val: string | string[]) => {
    const ids = Array.isArray(val) ? val : [];
    assetFilterForm.setValue('selectedAssetFilterTypes', ids);
    setSelectedAssetFilterTypes(ids);
    resetPaginationToFirstPage();
    throttledCallback();
  };

  const assetFilterTypeOptions = useMemo<DropdownOption[]>(() => {
    let items = AllAssetFilterTypeOptions;

    if (excludeAssetFilterTypes.length > 0) {
      items = _.differenceWith(AllAssetFilterTypeOptions, excludeAssetFilterTypes, _.isEqual);
    }

    return items.map((item) => ({
      ...item,
      id: item.id,
      label: item.name,
    }));
  }, [excludeAssetFilterTypes]);

  return (
    <div className="fullHeight fuel-asset-search">
      <Container className="fullHeight" component="section" maxWidth="lg">
        <Grid className="asset-search-controls" container spacing={2}>
          <Grid item xs={12}>
            <FuelSearchInput
              onSearch={onSearchClick}
              onChange={onSearchTextChanged}
              treeComponent={
                accessibleOUList.length > 0 ? (
                  <VNDropdown
                    compact
                    multiple
                    name="ouSelect"
                    placeholder="Portal(s)"
                    searchable="inset"
                    options={ouOptions}
                    control={ouForm.control}
                    listComponent={VNDropdownOUOptions}
                  />
                ) : null
              }
            />
          </Grid>
          <Grid item xs={12}>
            <FuelTags
              id="fuel-ingest-tag-id"
              tags={selectedTags}
              inputTagWidth={365}
              onChange={onTagsChanged}
              ouIds={(watchedOuSelect?.length > 0 ? watchedOuSelect : [ouData?.ouId]) as string[]}
            />
          </Grid>
          <Grid item xs={6}>
            <FormProvider {...categoryForm}>
              <VNAutocomplete
                name="selectedCategories"
                label="Categories"
                options={categoryOptions}
                valueProp="id"
                labelProp="label"
                multiple
                allowClear
                limitTags={1}
                searchable="inset"
                chipClassName="tag-chip cat"
                onChange={onCategoriesChanged}
                inputProps={{ placeholder: 'Select Categories' }}
              />
            </FormProvider>
          </Grid>

          <Grid item xs={6}>
            <FormProvider {...assetFilterForm}>
              <VNAutocomplete
                name="selectedAssetFilterTypes"
                label="Asset Types"
                options={assetFilterTypeOptions}
                valueProp="id"
                labelProp="label"
                multiple
                allowClear
                limitTags={1}
                optionTemplate={renderAssetTypeOption}
                onChange={onAssetFilterTypesChanged}
                inputProps={{ readOnly: true, placeholder: 'Select Asset Type' }}
              />
            </FormProvider>
          </Grid>
        </Grid>
        <div className="">
          {/* <FuelTable
            columnHelper={columnHelper}
            data={items}
            defaultColumns={columns}
            paginationDetail={pagination}
            paginationChange={paginationChange}
            showRowSelection={false}
            loading={loading}
            tableHeight={42}
            pull
            id="fuel-asset-search-table"
          /> */}
          <FormProvider {...vnTableMethods}>
            <VNTable
              columns={columns}
              paginationChange={paginationChange}
              externalDnd
              dataKey="data"
              tableId="asset-search-table"
              dragSourceOnly
              loading={tableLoading}
            />
          </FormProvider>
        </div>
      </Container>
    </div>
  );
};

export default FuelAssetSearch;
